import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, IconButton, Avatar, Paper, Button, Tooltip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Recorder from 'opus-recorder';
import { loadMedia, downloadMedia } from '../api/chat';

const MEDIA_PLACEHOLDERS = ['[Photo]', '[Video]', '[Audio]', '[Document]', '[Sticker]', '[Media]'];

const convertToJpeg = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      canvas.toBlob((b) => {
        URL.revokeObjectURL(url);
        if (!b) return reject(new Error('Image conversion failed'));
        resolve(new File([b], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.9);
    } catch (err) {
      URL.revokeObjectURL(url);
      reject(err);
    }
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('Unsupported image format'));
  };
  img.src = url;
});

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const formatTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const isWindowExpired = (messages) => {
  if (!messages || messages.length === 0) return false;
  const userMsgs = messages.filter(m => m.sender === 'user');
  if (userMsgs.length === 0) return true;
  const lastUserMsg = userMsgs[userMsgs.length - 1];
  const ts = lastUserMsg.timestamp || lastUserMsg.createdAt;
  if (!ts) return false;
  const diff = Date.now() - new Date(ts).getTime();
  return diff > 24 * 60 * 60 * 1000;
};

const MediaBubble = ({ msg, isAgent }) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const urlRef = useRef('');

  useEffect(() => {
    if (!msg.media) return;
    let active = true;
    loadMedia(msg.media)
      .then(u => {
        if (!active) return;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = u;
        setUrl(u);
      })
      .catch(err => {
        if (!active) return;
        const detail = err?.response?.data?.message || err.message || '';
        setError(detail.includes('Media fetch failed') || detail.includes('token') ? detail.slice(0, 180) : 'Media unavailable');
      });
    return () => { active = false; };
  }, [msg.media]);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  if (!url) {
    return (
      <Typography variant="caption" color={error ? '#dc2626' : '#667781'} sx={{ fontSize: 11, minWidth: 120, textAlign: 'center', py: 1, display: 'block', wordBreak: 'break-word' }}>
        {error || 'Loading…'}
      </Typography>
    );
  }

  if (msg.mediaType === 'image') {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={msg.caption || 'image'}
          style={{ display: 'block', maxWidth: 260, maxHeight: 320, borderRadius: 8, cursor: 'pointer' }} />
      </a>
    );
  }
  if (msg.mediaType === 'video') {
    return (
      <video src={url} controls style={{ display: 'block', maxWidth: 280, maxHeight: 320, borderRadius: 8 }} />
    );
  }
  if (msg.mediaType === 'audio') {
    return (
      <audio src={url} controls style={{ display: 'block', maxWidth: 260 }} />
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180, cursor: 'pointer' }}
      onClick={() => msg.media && downloadMedia(msg.media, msg.fileName || 'media')}>
      <InsertDriveFileIcon sx={{ color: isAgent ? '#075e54' : '#64748b', fontSize: 32, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" color="#111b21" sx={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {msg.fileName || 'Document'}
        </Typography>
        <Typography variant="caption" color="#667781" sx={{ fontSize: 10 }}>Tap to download</Typography>
      </Box>
    </Box>
  );
};

const ChatArea = ({ messages, sessionId, conversation, onSend, onSendMedia, currentUser, onExport, blocked, unsubscribed, onBlock, onUnblock, blocking }) => {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const [caption, setCaption] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recTime, setRecTime] = useState(0);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const recorderRef = useRef(null);
  const audioFileRef = useRef(null);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);

  const stopRecordingUi = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
    setRecTime(0);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const rec = new Recorder({
        encoderPath: `${process.env.PUBLIC_URL || ''}/encoderWorker.min.js`,
        streamPages: false,
        mono: true,
        encoderApplication: 2049,
        encoderFrameSize: 20,
      });
      recorderRef.current = rec;
      cancelledRef.current = false;
      rec.ondataavailable = (typedArray) => {
        if (cancelledRef.current) return;
        audioFileRef.current = new File([typedArray], 'voice-note.ogg', { type: 'audio/ogg' });
      };
      rec.onstop = async () => {
        const file = audioFileRef.current;
        audioFileRef.current = null;
        stopRecordingUi();
        if (!file || cancelledRef.current) return;
        setSending(true);
        try {
          await onSendMedia(file, '');
        } catch (err) {
          window.alert('Voice note send failed: ' + (err?.response?.data?.message || err.message || 'Unknown error'));
        } finally {
          setSending(false);
        }
      };
      rec.onerror = (err) => {
        console.error('Recorder error', err);
        window.alert('Recording error: ' + (err?.message || JSON.stringify(err)));
      };
      await rec.start();
      setRecording(true);
      setRecTime(0);
      timerRef.current = setInterval(() => setRecTime(t => t + 1), 1000);
    } catch (err) {
      window.alert('Microphone access denied. Allow mic permission to record voice notes.');
    }
  };

  const stopRecording = () => {
    if (recorderRef.current) recorderRef.current.stop();
  };

  const cancelRecording = () => {
    cancelledRef.current = true;
    const rec = recorderRef.current;
    if (rec) {
      if (typeof rec.cancel === 'function') rec.cancel();
      else rec.clearStream();
    }
    stopRecordingUi();
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => () => {
    if (filePreview) URL.revokeObjectURL(filePreview);
  }, [filePreview]);

  const handleSend = async () => {
    if (file) {
      if (!sending) {
        setSending(true);
        try { await onSendMedia(file, caption.trim()); } finally {
          setSending(false);
          setFile(null);
          setFilePreview('');
          setCaption('');
        }
      }
      return;
    }
    if (!text.trim()) return;
    onSend(text.trim());
    setText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = async (e) => {
    let f = e.target.files?.[0];
    if (!f) return;
    try {
      if (f.type.startsWith('image/') && !['image/jpeg', 'image/png'].includes(f.type)) {
        f = await convertToJpeg(f);
      }
      if (filePreview) URL.revokeObjectURL(filePreview);
      setFile(f);
      setFilePreview(URL.createObjectURL(f));
    } catch (err) {
      window.alert('Image not supported: ' + err.message);
    }
    e.target.value = '';
  };

  if (!sessionId) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f2f5' }}>
        <Box sx={{ textAlign: 'center' }}>
          <img src="/talktokam-green-wa.png" alt="Talktokam" style={{ height: 64, borderRadius: 8, marginBottom: 16, opacity: 0.4 }} />
          <Typography variant="h6" color="#64748b" fontWeight={500}>Select a chat to start messaging</Typography>
        </Box>
      </Box>
    );
  }

  const windowExpired = isWindowExpired(messages);
  const contactName = conversation?.userName || sessionId;
  const initials = getInitials(contactName);
  const phoneDisplay = sessionId?.length > 6 ? `+${sessionId}` : sessionId;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#efeae2', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 5L35 15H25L30 5ZM30 25L35 35H25L30 25Z\' fill=\'%23e0d6cc\' fill-opacity=\'0.4\'/%3E%3C/svg%3E")' }}>
      {/* Header */}
      <Box sx={{ p: 1.5, bgcolor: '#f0f2f5', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: '#075e54', fontSize: 14, fontWeight: 700 }}>{initials}</Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={700} fontSize={14} color="#1e293b">{contactName}</Typography>
          <Typography variant="caption" color="#64748b" fontSize={11}>{phoneDisplay}</Typography>
        </Box>
        {onExport && (
          <Button size="small" variant="text" onClick={() => onExport(sessionId)}
            sx={{ color: '#075e54', textTransform: 'none', fontSize: 12, fontWeight: 600, minWidth: 'auto' }}>
            Export
          </Button>
        )}
        {blocked ? (
          <Button size="small" variant="text" onClick={() => onUnblock(sessionId)} disabled={blocking}
            sx={{ color: '#075e54', textTransform: 'none', fontSize: 12, fontWeight: 700, minWidth: 'auto', border: '1px solid #075e54', borderRadius: 1.5, px: 1 }}>
            Unblock
          </Button>
        ) : (
          <Button size="small" variant="text" onClick={() => onBlock(sessionId)} disabled={blocking}
            sx={{ color: '#dc2626', textTransform: 'none', fontSize: 12, fontWeight: 600, minWidth: 'auto', border: '1px solid #dc2626', borderRadius: 1.5, px: 1 }}>
            {blocking ? 'Blocking…' : 'Block'}
          </Button>
        )}
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {(!messages || messages.length === 0) && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            No messages yet. Say hello!
          </Typography>
        )}
        {messages?.map((msg, i) => {
          const isAgent = msg.sender === 'agent' || msg.sender === currentUser;
          const t1 = new Date(msg.timestamp || msg.createdAt);
          const ok1 = !isNaN(t1.getTime());
          const t2 = messages[i - 1] ? new Date(messages[i - 1].timestamp || messages[i - 1].createdAt) : null;
          const showDate = i === 0 || !messages[i - 1] || (ok1 && (!t2 || isNaN(t2.getTime()) || t1.toDateString() !== t2.toDateString()));
          return (
            <React.Fragment key={i}>
              {showDate && (
                <Box sx={{ textAlign: 'center', my: 1 }}>
                  <Typography variant="caption" sx={{ bgcolor: 'rgba(225,245,254,0.92)', px: 1.5, py: 0.5, borderRadius: 1.5, color: '#54656f', fontSize: 11, fontWeight: 500, boxShadow: '0 1px 1px rgba(0,0,0,0.05)' }}>
                    {ok1 ? t1.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </Typography>
                </Box>
              )}
              {(msg.text || msg.media) && (
                <Box sx={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start', position: 'relative' }}>
                  <Box sx={{ position: 'relative', maxWidth: '70%' }}>
                    {/* Tail */}
                    <Box sx={{
                      position: 'absolute',
                      top: msg.media ? 8 : 0,
                      [isAgent ? 'right' : 'left']: -6,
                      width: 0, height: 0,
                      borderStyle: 'solid',
                      borderWidth: isAgent ? '0 0 12px 8px' : '0 8px 12px 0',
                      borderColor: isAgent
                        ? 'transparent transparent transparent #dcf8c6'
                        : 'transparent #fff transparent transparent',
                    }} />
                    <Paper elevation={0} sx={{
                      px: msg.media ? 0.8 : 1.5,
                      py: msg.media ? 0.8 : 0.8,
                      borderTopRightRadius: isAgent ? 1.5 : 2,
                      borderTopLeftRadius: isAgent ? 2 : 1.5,
                      borderBottomRightRadius: isAgent ? 0 : 2,
                      borderBottomLeftRadius: isAgent ? 2 : 0,
                      bgcolor: isAgent ? '#dcf8c6' : '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                      position: 'relative',
                      minWidth: msg.mediaType === 'document' ? 220 : undefined,
                    }}>
                      {msg.media && <MediaBubble msg={msg} isAgent={isAgent} />}
                      {msg.text && !(msg.media && MEDIA_PLACEHOLDERS.includes(msg.text)) && (
                        <Typography variant="body2" color="#111b21" sx={{ fontSize: 13.5, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word', pt: msg.media ? 0.5 : 0, pr: msg.media ? 0.5 : 4 }}>
                          {msg.text}
                        </Typography>
                      )}
                      <Typography variant="caption" color="#667781" sx={{ position: 'absolute', bottom: 6, right: 10, fontSize: 10, userSelect: 'none' }}>
                        {formatTime(msg.timestamp || msg.createdAt)}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              )}
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </Box>

      {/* 24hr Window Expired Notice */}
      {windowExpired && (
        <Box sx={{ p: 2, bgcolor: '#fff3cd', borderTop: '1px solid #ffc107', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: 20, flexShrink: 0 }} />
          <Typography variant="caption" color="#92400e" sx={{ flex: 1 }}>
            Your free 24-hour service window is over. To send messages, use a pre-approved template or ask the customer to message you first.
          </Typography>
          <Button size="small" variant="contained" href="https://business.facebook.com/wa/manage/payment" target="_blank"
            startIcon={<OpenInNewIcon />}
            sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: 11, bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' }, whiteSpace: 'nowrap' }}>
            Add Payment / Recharge
          </Button>
        </Box>
      )}

      {/* Blocked notice */}
      {blocked && (
        <Box sx={{ p: 2, bgcolor: '#fee2e2', borderTop: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <WarningAmberIcon sx={{ color: '#dc2626', fontSize: 20, flexShrink: 0 }} />
          <Typography variant="caption" color="#991b1b" sx={{ flex: 1 }}>
            You blocked this number — messages from this number are ignored and no replies are sent. Click "Unblock" to resume.
          </Typography>
        </Box>
      )}

      {/* Unsubscribed notice */}
      {unsubscribed && !blocked && (
        <Box sx={{ p: 2, bgcolor: '#fef2f2', borderTop: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <WarningAmberIcon sx={{ color: '#b91c1c', fontSize: 20, flexShrink: 0 }} />
          <Typography variant="caption" color="#991b1b" sx={{ flex: 1 }}>
            This contact has unsubscribed and cannot receive messages. They must send "START" to opt back in.
          </Typography>
        </Box>
      )}

      {/* Selected file preview */}
      {file && (
        <Box sx={{ p: 1, bgcolor: '#f0f2f5', borderTop: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
          {filePreview && (file.type || '').startsWith('image/') ? (
            <img src={filePreview} alt="preview" style={{ height: 48, width: 48, borderRadius: 8, objectFit: 'cover' }} />
          ) : (
            <InsertDriveFileIcon sx={{ color: '#64748b', fontSize: 36 }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="#1e293b" sx={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {file.name}
            </Typography>
            <TextField size="small" placeholder="Add a caption…" value={caption}
              onChange={e => setCaption(e.target.value)} fullWidth multiline minRows={1} maxRows={3}
              sx={{ mt: 0.5, bgcolor: '#fff', borderRadius: 1.5, '& .MuiOutlinedInput-root': { fontSize: 12.5 } }} />
          </Box>
          <IconButton size="small" onClick={() => { setFile(null); setFilePreview(''); setCaption(''); }} sx={{ color: '#64748b' }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      {/* Input */}
      <Box sx={{ p: 1.5, bgcolor: '#f0f2f5', display: 'flex', gap: 1, alignItems: 'center' }}>
        <input ref={fileInputRef} type="file" hidden accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar" onChange={handleFileSelect} />
        {recording ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#fff', borderRadius: 2, px: 1.5, py: 0.8, border: '1px solid #e0e0e0' }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#dc2626', animation: 'pulse 1s infinite' }} />
              <Typography variant="body2" color="#1e293b" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                {String(Math.floor(recTime / 60)).padStart(2, '0')}:{String(recTime % 60).padStart(2, '0')}
              </Typography>
            </Box>
            <Tooltip title="Cancel recording">
              <IconButton onClick={cancelRecording} sx={{ color: '#64748b' }}>
                <CloseIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Stop & send">
              <IconButton onClick={stopRecording} sx={{ bgcolor: '#dc2626', color: '#fff', '&:hover': { bgcolor: '#b91c1c' }, width: 40, height: 40 }}>
                <StopIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title="Attach image, video, audio or document">
              <IconButton onClick={() => fileInputRef.current?.click()} disabled={blocked || unsubscribed || windowExpired || sending} sx={{ color: '#075e54' }}>
                <AttachFileIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Record voice note">
              <IconButton onClick={startRecording} disabled={blocked || unsubscribed || windowExpired || sending} sx={{ color: '#075e54' }}>
                <MicIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
        <TextField fullWidth variant="outlined" placeholder={recording ? 'Recording…' : (blocked ? 'Number is blocked' : (unsubscribed ? 'User has unsubscribed' : 'Type a message'))} value={text}
          onChange={e => setText(e.target.value)} onKeyDown={handleKeyDown}
          size="small" multiline minRows={1} maxRows={5} sx={{ bgcolor: '#fff', borderRadius: 2 }}
          InputProps={{
            sx: { borderRadius: 2, '& fieldset': { borderColor: '#e0e0e0' } },
            disabled: windowExpired,
          }}
          disabled={blocked || unsubscribed || windowExpired || recording}
        />
        <IconButton onClick={handleSend} color="primary" disabled={(!text.trim() && !file) || blocked || unsubscribed || windowExpired || sending || recording}
          sx={{ bgcolor: (text.trim() || file) && !windowExpired ? '#075e54' : '#e0e0e0', color: (text.trim() || file) && !windowExpired ? '#fff' : '#94a3b8', borderRadius: '50%', width: 40, height: 40, '&:hover': { bgcolor: (text.trim() || file) ? '#054d44' : '#e0e0e0' } }}>
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatArea;
